import React, { useState, useEffect } from "react";
import serviceApi from "../../../../../shared/helpers/service-api";
import request from "axios";
import {
  TextField,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  CheckCircleTwoTone as CheckCircleIcon,
  Email as EmailIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import CommonDialog from "shared/components/dialogs/CommonDialog";
import "./style.scss";
import { rgbToHex } from "shared/helpers/ColorsHelper";
import { isEmail } from "shared/common-functions";

const AmbassadorForm = ({
  ambassadorId,
  primaryColor,
  secondaryColor,
  defaultCampaignId,
}) => {
  const root = document.documentElement;
  root.style.setProperty("--primary-color", rgbToHex(primaryColor));
  root.style.setProperty("--secondary-color", rgbToHex(secondaryColor));

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [firstName, setFirstName] = useState();
  const [lastName, setLastName] = useState();
  const [email, setEmail] = useState();
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [formResetKey, setFormResetKey] = useState(0);
  const [customFields, setCustomFields] = useState([]);
  const [customFieldValues, setCustomFieldValues] = useState({});
  const [errors, setErrors] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isError, setIsError] = useState(false);
  const [notificationSent, setNotificationSent] = useState(true);

  const { getBaseApiUrl, mapPostRequestParams } = serviceApi();

  // The section's configured default, or null when none is set or it is no
  // longer one of the section's campaigns.
  const findDefaultCampaign = (campaignList) =>
    campaignList.find((c) => c.id_string === defaultCampaignId) || null;

  useEffect(() => {
    if (!ambassadorId) {
      return;
    }

    fetchNextCampigns();
  }, []);

  const fetchNextCampigns = () => {
    setLoading(true);
    let url = `${getBaseApiUrl()}/microsite/api/campaigns/ambassador_campaign_list`;
    url += `?ambassador_id=${ambassadorId}`;
    request(url, {
      method: "GET",
      contentType: "application/json",
    }).then(({ data }) => {
      if (!data) {
        return;
      }

      const fetchedCampaigns = data.campaigns || [];
      setCampaigns(fetchedCampaigns);
      setCustomFields(data.custom_fields || []);
      setSelectedCampaign(findDefaultCampaign(fetchedCampaigns));
      setLoading(false);
    });
  };

  const validate = () => {
    const errors = {};

    if (!firstName) {
      errors["firstName"] = "First name is required";
    }
    if (!lastName) {
      errors["lastName"] = "Last name is required";
    }
    if (!email) {
      errors["email"] = "Email is required";
    }
    if (typeof email !== "undefined") {
      if (!isEmail(email)) {
        errors["email"] = "Please enter valid email address";
      }
    }
    if (!selectedCampaign) {
      errors["selectedCampaign"] = "Select campaign is required";
    }

    // Validate custom fields
    customFields.forEach((field) => {
      const fieldName = field.name;
      const fieldValue = customFieldValues[fieldName];
      if (field.required === true) {
        if (
          !fieldValue ||
          (Array.isArray(fieldValue) && fieldValue.length === 0) ||
          fieldValue === ""
        ) {
          errors[`custom_${fieldName}`] = `${field.label} is required`;
        }
      }
    });

    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const resetForm = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setSelectedCampaign(findDefaultCampaign(campaigns));
    setCustomFieldValues({});
    setErrors({});

    // Force reset of Autocomplete
    setFormResetKey((k) => k + 1);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    const isFormValid = validate();
    if (isFormValid) {
      const token = document.querySelector('meta[name="csrf-token"]').content;
      const params = {
        first_name: firstName,
        last_name: lastName,
        email: email,
        campaign_id: selectedCampaign.id_string,
        role: "participant",
        custom_fields: customFieldValues,
      };
      request("/user_campaigns/create", mapPostRequestParams(token, params))
        .then((response) => {
          const notificationSentResponse = response.data.notification_sent === true;
          setNotificationSent(notificationSentResponse);
          setIsError(false);
          setDialogOpen(true);

          resetForm();

          setTimeout(() => {
            setLoading(false);
          }, 1000);
        })
        .catch(() => {
          setIsError(true);
          setDialogOpen(true);
          setTimeout(() => {
            setLoading(false);
          }, 1000);
        });
    } else {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  return (
    <div className="amba-form">
      <div className="form-top-line" />
      <div>
        <h2 className="title" style={{ color: rgbToHex(primaryColor) }}>
          Be an Ambassador
        </h2>
      </div>
      <div className="sub-title-root">
        <span className="sub-title">
          Fill out your details and we’ll get back to you as soon as possible
        </span>
      </div>
      <form data-testid="t-ambassa-compone--Ambassa-form" onSubmit={handleSubmit}>
        <TextField
          data-testid="t-ambassa-compone--Ambassa-firstname"
          aria-label="first name required"
          className="input-text"
          name="firstName"
          type="text"
          value={firstName}
          autoComplete="given-name"
          onChange={(e) => setFirstName(e.target.value)}
          label="First Name"
          required
          error={errors.firstName}
          helperText={errors.firstName}
        />
        <TextField
          data-testid="t-ambassa-compone--Ambassa-lastname"
          aria-label="last name required"
          className="input-text"
          name="lastName"
          type="text"
          value={lastName}
          autoComplete="family-name"
          onChange={(e) => setLastName(e.target.value)}
          label="Last Name"
          required
          error={errors.lastName}
          helperText={errors.lastName}
        />
        <TextField
          data-testid="t-ambassa-compone--Ambassa-email"
          aria-label="email required"
          className="input-text"
          name="email"
          type="email"
          value={email}
          autoComplete="email"
          onChange={(e) => setEmail(e.target.value)}
          label="Email"
          required
          error={errors.email}
          helperText={errors.email}
        />
        <div className="campaign-field">
          <Autocomplete
            key={formResetKey}
            id="ambassador-campaigns"
            value={selectedCampaign}
            options={campaigns}
            onChange={(e, value) => {
              setSelectedCampaign(value);
            }}
            getOptionLabel={(option) => option.name || ""}
            renderInput={(params) => (
              <TextField
                data-testid="t-ambassa-compone--Ambassa-search"
                {...params}
                variant="outlined"
                label="Which campaign do you want to join?"
                required
                placeholder="Search"
                error={errors.selectedCampaign}
                helperText={errors.selectedCampaign}
              />
            )}
          />
        </div>
        {customFields.map((field) => {
          const fieldName = field.name;
          const fieldValue = customFieldValues[fieldName] || (field.multiple ? [] : "");
          const isRequired = field.required === true;
          const hasError = errors[`custom_${fieldName}`];

          if (field.html_control === "input") {
            return (
              <TextField
                key={fieldName}
                className="input-text"
                name={fieldName}
                type="text"
                value={fieldValue}
                onChange={(e) => {
                  setCustomFieldValues({
                    ...customFieldValues,
                    [fieldName]: e.target.value,
                  });
                  // Clear error when user types
                  if (errors[`custom_${fieldName}`]) {
                    setErrors({
                      ...errors,
                      [`custom_${fieldName}`]: undefined,
                    });
                  }
                }}
                label={field.label}
                error={!!hasError}
                helperText={hasError}
                required={isRequired}
              />
            );
          } else if (field.html_control === "select") {
            const options = field.entity_custom_values || [];
            const htmlId = `custom-field-${fieldName}`;
            const labelId = `${htmlId}-label`;
            if (field.multiple) {
              return (
                <FormControl
                  key={fieldName}
                  className="input-text"
                  error={!!hasError}
                  required={isRequired}>
                  <InputLabel htmlFor={htmlId} id={labelId}>
                    {field.label}
                  </InputLabel>
                  <Select
                    multiple
                    value={Array.isArray(fieldValue) ? fieldValue : []}
                    onChange={(e) => {
                      setCustomFieldValues({
                        ...customFieldValues,
                        [fieldName]: e.target.value,
                      });
                      if (errors[`custom_${fieldName}`]) {
                        setErrors({
                          ...errors,
                          [`custom_${fieldName}`]: undefined,
                        });
                      }
                    }}
                    label={field.label}
                    labelId={labelId}
                    inputProps={{
                      id: htmlId
                    }}
                    renderValue={(selected) => selected.join(", ")}>
                    {options.map((option) => (
                      <MenuItem key={option.name || option} value={option.name || option}>
                        {option.name || option}
                      </MenuItem>
                    ))}
                  </Select>
                  {hasError && (
                    <div
                      style={{ color: "#d32f2f", fontSize: "0.75rem", marginTop: "3px" }}>
                      {hasError}
                    </div>
                  )}
                </FormControl>
              );
            } else {
              return (
                <FormControl
                  key={fieldName}
                  className="input-text"
                  error={!!hasError}
                  required={isRequired}>
                  <InputLabel htmlFor={htmlId} id={labelId}>
                    {field.label}
                  </InputLabel>
                  <Select
                    value={fieldValue}
                    onChange={(e) => {
                      setCustomFieldValues({
                        ...customFieldValues,
                        [fieldName]: e.target.value,
                      });
                      if (errors[`custom_${fieldName}`]) {
                        setErrors({
                          ...errors,
                          [`custom_${fieldName}`]: undefined,
                        });
                      }
                    }}
                    label={field.label}
                    labelId={labelId}
                    inputProps={{
                      id: htmlId
                    }}
                  >
                    <MenuItem value="">
                      <em>----</em>
                    </MenuItem>
                    {options.map((option) => (
                      <MenuItem key={option.name || option} value={option.name || option}>
                        {option.name || option}
                      </MenuItem>
                    ))}
                  </Select>
                  {hasError && (
                    <div
                      style={{ color: "#d32f2f", fontSize: "0.75rem", marginTop: "3px" }}>
                      {hasError}
                    </div>
                  )}
                </FormControl>
              );
            }
          } else if (field.html_control === "boolean") {
            return (
              <FormControlLabel
                key={fieldName}
                control={
                  <Checkbox
                    checked={fieldValue === true || fieldValue === "true"}
                    onChange={(e) => {
                      setCustomFieldValues({
                        ...customFieldValues,
                        [fieldName]: e.target.checked.toString(),
                      });
                    }}
                  />
                }
                label={field.label}
              />
            );
          }
          return null;
        })}
        <div className="required-text">* denotes required fields</div>
        <div className="btn-root">
          <button
            data-testid="t-ambassa-compone--Ambassa-submit"
            type="submit"
            className="btn-submit button_ambassador"
            disabled={loading}>
            {loading ? (
              <i className="fa fa-2x fa-refresh fa-spin" />
            ) : (
              "BECOME AN AMBASSADOR"
            )}
          </button>
        </div>
      </form>
      <CommonDialog open={dialogOpen} onClose={handleCloseDialog} title="">
        <div className="ambassador-dialog-content">
          {isError ? (
            <ErrorIcon sx={{ fontSize: 120, color: "#f44336" }} />
          ) : notificationSent ? (
            <div className="ambassador-dialog-container">
              <div className="ambassador-dialog-circle">
                <EmailIcon sx={{ fontSize: 120, color: "#666" }} />
              </div>
            </div>
          ) : (
            <CheckCircleIcon sx={{ fontSize: 60, color: "#4caf50" }} />
          )}

          <div className="dialog-title">
            {isError
              ? "Server Error"
              : notificationSent
                ? "Your details were sent successfully!"
                : "Registration Complete"}
          </div>

          <div className="dialog-description">
            {isError
              ? "Sorry for the inconvenience, we're currently dealing with a bug and we'll be back soon."
              : notificationSent
                ? "We will get back to you with confirmation as soon as possible"
                : "You are now registered as an ambassador!"}
          </div>
        </div>
      </CommonDialog>
    </div>
  );
};

export default AmbassadorForm;
